/**
 * The DOMPurify hook that clears `<style>`/`<script>` contents is now owned by
 * `@west-tennessee-consulting/email/sanitize` (single source shared with the WTC mail client), applied
 * automatically by the shared `SafeEmailBody` renderer. Re-exported here so any
 * `body-utils` consumer keeps resolving it.
 */
export { registerDomPurifyEmailBodyHooks } from "@west-tennessee-consulting/email/sanitize";

/**
 * Remove markup blocks whose inner text would appear on screen after sanitization.
 * DOMPurify drops tags like `style`/`script` but with KEEP_CONTENT may leave their
 * inner text as visible nodes — common cause of raw CSS in the timeline/detail UI.
 */
export function stripEmailSanitizerLeakSources(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }
  let s = html;
  s = s.replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, "");
  s = s.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");
  s = s.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "");
  s = s.replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, "");
  s = s.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, "");
  s = s.replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, "");
  s = s.replace(/<template\b[^>]*>[\s\S]*?<\/template>/gi, "");
  s = s.replace(/<link\b[^>]*\brel\s*=\s*["']?\s*stylesheet[^>]*>/gi, "");
  s = s.replace(/<style\b[^>]*>[\s\S]*$/gi, "");
  s = s.replace(/<script\b[^>]*>[\s\S]*$/gi, "");
  return s;
}

/**
 * Sync pipelines sometimes store the newsletter `<style>…</style>` block **without** wrapper tags
 * (raw CSS pasted before the visible prose). {@link isHtmlContent} is then false and the CSS was
 * shown as “plaintext”. Cut from the first strong prose/HTML boundary after rule-like `{…}` blocks.
 */
export function stripLeadingDetachedCssBlob(html: string): string {
  const s = html.trimStart();
  if (!s || s.startsWith("<")) {
    return html;
  }

  const startsLikeCss =
    /^\/\*/.test(s) ||
    /^@media\b/m.test(s) ||
    /^body\s*\{/m.test(s) ||
    /^html\s*\{/m.test(s) ||
    /^\.[a-zA-Z_-][\w-]*\s*\{/.test(s);

  if (!startsLikeCss) {
    return html;
  }

  const boundaries = [
    /\}\s*(?=Hello\b)/i,
    /\}\s*(?=Dear\b)/i,
    /\}\s*(?=Hi\b)/i,
    /\}\s*(?=If\s+you\b)/i,
    /\}\s*(?=Good\s+(?:morning|afternoon|evening)\b)/i,
    /\}\s*(?=See\s+you\b)/i,
    /\}\s*(?=Thank\s+you\b)/i,
    /\}\s*(?=This\s+email\b)/i,
    /\}\s*(?=Date\s*&)/i,
    /\}\s*(?=Google\s+Meeting\b)/i,
    /\}\s*(?=<[a-z!?])/i,
  ];

  /** Ignore transitions in the first chars — avoids bogus hits inside short blobs / odd fragments. */
  const minCssPrefix = 320;
  let bestStart = -1;
  let cutAt = -1;
  for (const re of boundaries) {
    const m = re.exec(s);
    if (m !== null && m.index !== undefined && m.index >= minCssPrefix) {
      const after = m.index + m[0].length;
      if (bestStart === -1 || m.index < bestStart) {
        bestStart = m.index;
        cutAt = after;
      }
    }
  }

  if (cutAt < minCssPrefix) {
    return html;
  }

  const tail = s.slice(cutAt).trimStart();
  return tail.length >= 16 ? tail : html;
}

/**
 * Uses the browser HTML parser to drop subtree roots that must never reach `dangerouslySetInnerHTML`,
 * including malformed fragments where regex stripping missed a `<style>` boundary.
 */
export function removeDangerousParsedHtmlNodes(html: string): string {
  if (!html || typeof DOMParser === "undefined") {
    return html;
  }
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc
      .querySelectorAll("style, script, link[rel=\"stylesheet\"], noscript, iframe")
      .forEach((el) => el.remove());

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

